import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useSortable } from '../../hooks/useSortable';
import { usePaginacao } from '../../hooks/usePaginacao';
import { formatDate } from '../../utils/date';
import { formatMoeda } from '../../utils/fmt';
import StatusBadge from '../../components/common/StatusBadge';
import { useAuth } from '../../contexts/AuthContext';
import * as api from '../../api/pedidos';
import * as clientesApi from '../../api/clientes';
import * as empresasApi from '../../api/empresas';

export default function Pedidos() {
  const { user } = useAuth();
  const souAdmin = user?.perfil === 'admin';
  const emGlobal = souAdmin && !user?.empresa_ativa_id;

  const [items, setItems] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [idEmpresaFiltro, setIdEmpresaFiltro] = useState('');
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [filtroCliente, setFiltroCliente] = useState('');
  const [erroCarregar, setErroCarregar] = useState('');
  const navigate = useNavigate();

  const carregar = useCallback(() => {
    setLoading(true);
    setErroCarregar('');
    Promise.all([api.listar(), clientesApi.listar()])
      .then(([p, c]) => { setItems(p.data); setClientes(c.data); })
      .catch(() => setErroCarregar('Erro ao carregar dados. Recarregue a página.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    if (souAdmin) empresasApi.listEmpresas().then(r => setEmpresas(r.data)).catch(() => {});
  }, [souAdmin]);

  const clienteNome = id => clientes.find(c => c.id_cliente_final === id)?.nome || id;
  const empresaNome = id => empresas.find(e => e.id_empresa_operadora === id)?.nome || '—';

  const filtrados = items.filter(i => {
    const q = busca.toLowerCase();
    const matchQ = i.numero_pedido.toLowerCase().includes(q) || clienteNome(i.id_cliente_final).toLowerCase().includes(q);
    const matchC = filtroCliente ? i.id_cliente_final === filtroCliente : true;
    const matchE = emGlobal && idEmpresaFiltro ? i.id_empresa_operadora === idEmpresaFiltro : true;
    return matchQ && matchC && matchE;
  });

  const { sorted, Th } = useSortable(filtrados, 'data_pedido');
  const { paginados, Paginacao } = usePaginacao(sorted);

  const clienteOpts = [{ value: '', label: 'Todos os clientes' },
    ...clientes.map(c => ({ value: c.id_cliente_final, label: c.nome }))];
  const empresaOpts = [{ value: '', label: 'Todas as empresas' },
    ...empresas.map(e => ({ value: e.id_empresa_operadora, label: e.nome }))];

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <PageHeader titulo="Pedidos" subtitulo={`${items.length} pedidos`} />

      {erroCarregar && <div className="alert alert-danger py-2">{erroCarregar}</div>}

      <div className="d-flex gap-2 mb-3 flex-wrap">
        <input className="form-control" style={{ maxWidth: 280 }}
          placeholder="Buscar por número ou cliente..."
          value={busca} onChange={e => setBusca(e.target.value)} />
        <select className="form-select" style={{ maxWidth: 220 }}
          value={filtroCliente} onChange={e => setFiltroCliente(e.target.value)}>
          {clienteOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {emGlobal && (
          <select className="form-select" style={{ maxWidth: 220 }}
            value={idEmpresaFiltro} onChange={e => setIdEmpresaFiltro(e.target.value)}>
            {empresaOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        )}
      </div>

      <div className="card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <Th campo="numero_pedido">Número</Th>
                  {emGlobal && !idEmpresaFiltro && <th>Empresa</th>}
                  <Th campo="id_cliente_final">Cliente</Th>
                  <Th campo="data_pedido">Data</Th>
                  <Th campo="valor_total">Total</Th>
                  <th>Peças</th>
                  <th>Status</th>
                  <th style={{ width: 60 }}></th>
                </tr>
              </thead>
              <tbody>
                {paginados.length === 0 && (
                  <tr><td colSpan={emGlobal && !idEmpresaFiltro ? 8 : 7} className="text-center text-muted py-4">Nenhum pedido encontrado.</td></tr>
                )}
                {paginados.map(p => (
                  <tr key={p.id_pedido}>
                    <td className="fw-bold" style={{ color: 'var(--cjc-primary)' }}>{p.numero_pedido}</td>
                    {emGlobal && !idEmpresaFiltro && (
                      <td style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {empresaNome(p.id_empresa_operadora)}
                      </td>
                    )}
                    <td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {clienteNome(p.id_cliente_final)}
                    </td>
                    <td>{formatDate(p.data_pedido)}</td>
                    <td className="fw-semibold">{formatMoeda(p.valor_total)}</td>
                    <td>{p.itens?.reduce((s, i) => s + i.quantidade, 0) ?? '—'}</td>
                    <td><StatusBadge status={p.status} /></td>
                    <td>
                      <button className="btn btn-sm btn-outline-primary py-0 px-2"
                        onClick={() => navigate(`/pedidos/${p.id_pedido}`)}>Ver</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <Paginacao />
    </div>
  );
}
